import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { updateUserData } from '../firebase/helpers';
import bcrypt from 'bcryptjs';

function UpdateTransactionPasswordPage({ onBack }) {
  const { user } = useAuth();
  const { userProfile } = useUserProfile(user?.uid);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleUpdate = async () => {
    setError('');
    setSuccess(false);

    // Validation
    if (!oldPassword) {
      setError('Current transaction password is required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (oldPassword === newPassword) {
      setError('New password must be different from the current password');
      return;
    }

    setLoading(true);

    try {
      // Verify old password
      if (!userProfile?.transactionPassword) {
        throw new Error('Transaction password not set. Please contact support.');
      }

      const isValid = await bcrypt.compare(oldPassword, userProfile.transactionPassword);
      if (!isValid) {
        throw new Error('Current transaction password is incorrect');
      }

      // Hash new password
      const hashedNewPassword = await bcrypt.hash(newPassword, 10);

      // Update password
      await updateUserData(user.uid, {
        transactionPassword: hashedNewPassword,
        transactionPasswordAttempts: 0,
        transactionPasswordLockoutUntil: null
      });

      setSuccess(true);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to update transaction password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="update-password-page">
      {/* Top Navigation Bar */}
      <div className="update-password-top-bar">
        <div className="update-password-back-btn" onClick={onBack}>
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="update-password-title-text">Update Transaction Password</div>
        <div className="update-password-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="update-password-content">
        {error && (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <i className="bi bi-check-circle me-2"></i>
            Transaction password updated successfully!
          </div>
        )}

        {/* Old Password Field */}
        <div className="update-password-field-group">
          <label className="update-password-label">Current Transaction Password</label>
          <input
            type="password"
            className="update-password-input"
            placeholder="Enter current transaction password"
            value={oldPassword}
            onChange={(e) => {
              setOldPassword(e.target.value);
              setError('');
            }}
            disabled={loading}
          />
        </div>

        {/* New Password Field */}
        <div className="update-password-field-group">
          <label className="update-password-label">New Transaction Password</label>
          <input
            type="password"
            className="update-password-input"
            placeholder="Enter new transaction password (min 6 characters)"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setError('');
            }}
            minLength={6}
            disabled={loading}
          />
          <small className="text-muted">Must be at least 6 characters long</small>
        </div>

        {/* Confirm New Password Field */}
        <div className="update-password-field-group">
          <label className="update-password-label">Confirm New Transaction Password</label>
          <input
            type="password"
            className="update-password-input"
            placeholder="Confirm new transaction password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setError('');
            }}
            minLength={6}
            disabled={loading}
          />
        </div>

        {/* Update Button */}
        <button 
          className="update-password-btn" 
          onClick={handleUpdate}
          disabled={loading || !oldPassword || !newPassword || !confirmPassword}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Updating...
            </>
          ) : (
            <>
              <i className="bi bi-key me-2"></i>
              Update Transaction Password
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default UpdateTransactionPasswordPage;

